import { useEffect, useState } from 'react'
import { supabase } from '../../../api/supabase'
import CustomerCombobox from '../../../components/CustomerCombobox'
import CardPortsPicker from '../../../components/pricing/CardPortsPicker'
import type { PortOption } from '../../../lib/cardPorts'
import type { PricingRateCard, QuoteTemplate } from './types'

// The two ways a customer quote comes into existence (20261003c): from a standard template (every line
// empty, staff only type prices), or by copying an existing card (everything comes along, only the
// customer / name / date change). Both are single RPC calls, so a failure leaves nothing half-created.

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white'
const labelCls = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1'
const today = () => new Date().toISOString().slice(0, 10)

function Shell({ title, onClose, children }: { title: string; onClose: () => void; children: React.ReactNode }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg my-10">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none" aria-label="Close">×</button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
    </div>
  )
}

function Actions({ busy, disabled, label, onClose, onSubmit }: { busy: boolean; disabled: boolean; label: string; onClose: () => void; onSubmit: () => void }) {
  return (
    <div className="flex justify-end gap-3 pt-2">
      <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">Cancel</button>
      <button onClick={onSubmit} disabled={busy || disabled} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">{busy ? 'Working…' : label}</button>
    </div>
  )
}

export function NewFromTemplateDialog({ onClose, onCreated }: { onClose: () => void; onCreated: (cardId: string) => void }) {
  const [templates, setTemplates] = useState<QuoteTemplate[]>([])
  const [lineCounts, setLineCounts] = useState<Record<string, number>>({})
  const [templateId, setTemplateId] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [name, setName] = useState('')
  const [from, setFrom] = useState(today())
  const [ports, setPorts] = useState<PortOption[]>([])
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    ;(async () => {
      const [{ data: t }, { data: l }] = await Promise.all([
        supabase.from('pricing_quote_templates').select('*').eq('is_active', true).order('name'),
        supabase.from('pricing_quote_template_lines').select('template_id'),
      ])
      const rows = (t as QuoteTemplate[]) || []
      setTemplates(rows)
      if (rows.length === 1) setTemplateId(rows[0].id)
      const counts: Record<string, number> = {}
      for (const r of (l as { template_id: string }[]) || []) counts[r.template_id] = (counts[r.template_id] || 0) + 1
      setLineCounts(counts)
    })()
  }, [])

  const submit = async () => {
    setBusy(true); setError(null)
    const { data, error: err } = await supabase.rpc('create_rate_card_from_template', {
      p_template_id: templateId, p_customer_id: customerId, p_rate_card_name: name.trim() || null,
      p_effective_from: from || null, p_port_ids: ports.map(p => p.id), p_delivery_method_id: null,
    })
    setBusy(false)
    if (err) { setError(err.message); return }
    onCreated(data as string)
  }

  return (
    <Shell title="New rate card from template" onClose={onClose}>
      {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
      <div>
        <label className={labelCls}>Template *</label>
        <select className={inputCls} value={templateId} onChange={e => setTemplateId(e.target.value)}>
          <option value="">— Select —</option>
          {templates.map(t => <option key={t.id} value={t.id}>{t.name_zh ? `${t.name_zh} — ` : ''}{t.name} ({lineCounts[t.id] ?? 0} lines)</option>)}
        </select>
        {templates.length === 0 && <p className="mt-1 text-xs text-amber-600">No templates found. Run migration 20261003b_quote_templates.sql.</p>}
        <p className="mt-1 text-xs text-gray-400">Every line is created without a price. You only fill in the numbers.</p>
      </div>
      <div>
        <label className={labelCls}>Customer *</label>
        <CustomerCombobox value={customerId} onChange={setCustomerId} placeholder="— Select customer —" />
      </div>
      <div>
        <label className={labelCls}>Ports <span className="text-gray-400 font-normal">(discharge ports the quote is for, optional)</span></label>
        <CardPortsPicker value={ports} onChange={setPorts} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>Rate card name <span className="text-gray-400 font-normal">(blank = automatic)</span></label>
          <input className={inputCls} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Effective from</label>
          <input type="date" className={inputCls} value={from} onChange={e => setFrom(e.target.value)} />
        </div>
      </div>
      <Actions busy={busy} disabled={!templateId || !customerId} label="Create draft" onClose={onClose} onSubmit={submit} />
    </Shell>
  )
}

export function CloneCardDialog({ source, sourceCustomerName, onClose, onCloned }: {
  source: PricingRateCard; sourceCustomerName: string; onClose: () => void; onCloned: (cardId: string) => void
}) {
  const [customerId, setCustomerId] = useState('')
  const [name, setName] = useState('')
  const [from, setFrom] = useState(today())
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true); setError(null)
    const { data, error: err } = await supabase.rpc('clone_pricing_rate_card', {
      p_source_card_id: source.id, p_customer_id: customerId, p_rate_card_name: name.trim() || null, p_effective_from: from || null,
    })
    setBusy(false)
    if (err) { setError(err.message); return }
    onCloned(data as string)
  }

  return (
    <Shell title="Copy rate card" onClose={onClose}>
      {error && <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm">{error}</div>}
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Copying <span className="font-medium">{source.rate_card_name}</span> ({sourceCustomerName}). Everything comes along —
        lines, tiers, free allowances, switched-off lines, at-cost / optional flags, ports. The copy is a <span className="font-medium">DRAFT</span> with no end date;
        then just change the prices.
      </p>
      <div>
        <label className={labelCls}>Copy to customer *</label>
        <CustomerCombobox value={customerId} onChange={setCustomerId} placeholder="— Select customer —" />
        {source.customer_id && (
          <button type="button" onClick={() => setCustomerId(source.customer_id!)} className="mt-1 text-xs text-blue-600 dark:text-blue-400 hover:underline">
            Same customer — make a new version ({sourceCustomerName})
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelCls}>New name <span className="text-gray-400 font-normal">(blank = automatic)</span></label>
          <input className={inputCls} value={name} onChange={e => setName(e.target.value)} />
        </div>
        <div>
          <label className={labelCls}>Effective from</label>
          <input type="date" className={inputCls} value={from} onChange={e => setFrom(e.target.value)} />
        </div>
      </div>
      <Actions busy={busy} disabled={!customerId} label="Copy as draft" onClose={onClose} onSubmit={submit} />
    </Shell>
  )
}
